import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState, type FormEvent } from "react";
import { indexersApi } from "@/api/indexers";
import {
  searchApi,
  type SearchResultRelease,
  type SearchResponse,
} from "@/api/search";
import { Button } from "@/components/ui/Button";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { categoryNames } from "@/lib/newznabCategories";
import { cn } from "@/lib/cn";

/**
 * Newznab category catalogue we surface in the picker. The full list
 * is huge (every sub-genre under Music has a four-digit code) — these
 * are the parents users actually search by. Children are sent to the
 * indexer too because it expands them server-side.
 */
const CATEGORY_PRESETS: { value: number; label: string }[] = [
  { value: 2000, label: "Movies" },
  { value: 5000, label: "TV" },
  { value: 3000, label: "Music" },
  { value: 6000, label: "Books" },
  { value: 7000, label: "PC" },
  { value: 8000, label: "Other" },
];

export function Search() {
  const { toast } = useToast();
  const [query, setQuery] = useState("");
  const [selectedIndexers, setSelectedIndexers] = useState<Set<number>>(
    new Set(),
  );
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(
    new Set(),
  );
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  // Migration 0062 + search-private: page-level destination toggle.
  // "public" routes downloads through the auto-detected category;
  // "private" overrides every Download click in this session to use
  // the configured private category, which the download client should
  // be set up to write to the private library root. Default public so
  // the common case stays one click.
  const [destination, setDestination] = useState<"public" | "private">(
    "public",
  );
  // Size filter (issue: "we need more filtering on our search page; add
  // support for filtering by size"). UI inputs are MB; we convert to
  // bytes at submit time. Empty string = no bound.
  const [minSizeMb, setMinSizeMb] = useState("");
  const [maxSizeMb, setMaxSizeMb] = useState("");
  // Pagination state. The Newznab spec supports offset+limit; we pass
  // both through and let each indexer paginate independently. With one
  // indexer selected this is exact; with multiple, page boundaries are
  // approximate but the user gets a "next page" affordance regardless.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  // Anchor at the top of the results block — the bottom pager scrolls
  // back here after a page change so the user lands on the first row.
  const resultsTopRef = useRef<HTMLDivElement>(null);

  const indexers = useQuery({
    queryKey: ["indexers"],
    queryFn: indexersApi.list,
  });

  const enabledIndexers = useMemo(
    () => (indexers.data ?? []).filter((i) => i.enabled),
    [indexers.data],
  );

  /** Fire a search at a given page offset. Page boundaries map to
   *  Newznab `offset = page * pageSize`; the backend just passes both
   *  values straight to each indexer. Reused by the form submit and by
   *  the pagination Prev/Next buttons. */
  const runSearch = async (nextPage: number) => {
    if (!query.trim()) {
      toast("Enter a search query", "error");
      return;
    }
    // Validate size inputs. We accept integers (MB); negatives or
    // garbage fall through as "no bound" rather than 4xx the call.
    const minBytes = parseMbToBytes(minSizeMb);
    const maxBytes = parseMbToBytes(maxSizeMb);
    if (
      minBytes != null &&
      maxBytes != null &&
      maxBytes > 0 &&
      minBytes > maxBytes
    ) {
      toast("Min size cannot exceed max size", "error");
      return;
    }
    setSearching(true);
    if (nextPage === 0) {
      // Only reset between distinct queries — page transitions keep
      // the result table mounted so the loading state is less jarring.
      setResults(null);
    }
    setSelectedRows(new Set());
    try {
      const res = await searchApi.search({
        q: query.trim(),
        indexer_ids:
          selectedIndexers.size > 0 ? Array.from(selectedIndexers) : undefined,
        categories:
          selectedCategories.size > 0
            ? Array.from(selectedCategories)
            : undefined,
        limit: pageSize,
        offset: nextPage * pageSize,
        min_size: minBytes ?? undefined,
        max_size: maxBytes ?? undefined,
      });
      setResults(res);
      setPage(nextPage);
      if (res.releases.length === 0) {
        toast(
          nextPage === 0 ? "No matching releases" : "No more results",
          "info",
        );
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : "Search failed", "error");
    } finally {
      setSearching(false);
    }
  };

  const onSearch = async (e: FormEvent) => {
    e.preventDefault();
    // Submitting the form always resets to page 0 — the user may have
    // changed query / filters and we don't want to land them on page 5
    // of a different result set.
    await runSearch(0);
  };

  const grabMut = useMutation({
    mutationFn: (release: SearchResultRelease) =>
      searchApi.grab(release, { private: destination === "private" }),
    onSuccess: (res, release) => {
      const tag = destination === "private" ? " (private)" : "";
      toast(
        `Sent "${release.title}" to ${res.client_name} (${res.external_id})${tag}`,
        "success",
      );
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : "Grab failed", "error"),
  });

  const toggleIndexer = (id: number) => {
    setSelectedIndexers((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleCategory = (id: number) => {
    setSelectedCategories((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRow = (guid: string) => {
    setSelectedRows((s) => {
      const next = new Set(s);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Search"
        description="Manual search across configured indexers. Save the NZB to disk or send it straight to the embedded downloader."
      />

      <form onSubmit={onSearch} className="space-y-4">
        <Field label="Query">
          <TextInput
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "Mumford and Sons Babel"'
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Indexers"
            hint={
              enabledIndexers.length === 0
                ? "No enabled indexers — configure one first."
                : "Leave all unchecked to search every enabled indexer."
            }
          >
            <div className="flex flex-wrap gap-2">
              {enabledIndexers.map((idx) => (
                <label
                  key={idx.id}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded border border-line bg-surface/40 px-2 py-1 text-xs",
                    selectedIndexers.has(idx.id) &&
                      "border-accent bg-accent/10",
                  )}
                >
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={selectedIndexers.has(idx.id)}
                    onChange={() => toggleIndexer(idx.id)}
                  />
                  {idx.name}
                </label>
              ))}
            </div>
          </Field>

          <Field
            label="Categories"
            hint="Newznab top-level categories. Indexers expand to sub-categories server-side."
          >
            <div className="flex flex-wrap gap-2">
              {CATEGORY_PRESETS.map((c) => (
                <label
                  key={c.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded border border-line bg-surface/40 px-2 py-1 text-xs",
                    selectedCategories.has(c.value) &&
                      "border-accent bg-accent/10",
                  )}
                >
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={selectedCategories.has(c.value)}
                    onChange={() => toggleCategory(c.value)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </Field>
        </div>

        {/* Size band + per-indexer page size. The Newznab spec
            supports `minsize` / `maxsize` server-side, so we pass
            them through and let each indexer filter before sending
            results back. Page size maps to Newznab `limit` and is
            also used by the Prev/Next pagination buttons below the
            results table. */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Field
            label="Min size (MB)"
            hint="Empty = no lower bound."
          >
            <NumberInput
              min={0}
              step="any"
              placeholder="e.g. 100"
              value={minSizeMb}
              onChange={(e) => setMinSizeMb(e.target.value)}
            />
          </Field>
          <Field
            label="Max size (MB)"
            hint="Empty = no upper bound."
          >
            <NumberInput
              min={0}
              step="any"
              placeholder="e.g. 5000"
              value={maxSizeMb}
              onChange={(e) => setMaxSizeMb(e.target.value)}
            />
          </Field>
          <Field
            label="Per-indexer limit"
            hint="Newznab `limit`. Caps the per-page request to each indexer."
          >
            <Select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </Select>
          </Field>
        </div>

        <Field
          label="Library destination"
          hint={
            destination === "private"
              ? "Each Download click sends with the configured private category — the matching NZBGet category must be set up to write to your private library root. The household media server never sees these files."
              : "Each Download click sends with the auto-detected category (movies / tv / music). Switch to Private to route grabs into the household-isolated library."
          }
        >
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDestination("public")}
              aria-pressed={destination === "public"}
              className={cn(
                "rounded border px-3 py-1.5 text-sm transition",
                destination === "public"
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong",
              )}
            >
              Public library
            </button>
            <button
              type="button"
              onClick={() => setDestination("private")}
              aria-pressed={destination === "private"}
              className={cn(
                "flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm transition",
                destination === "private"
                  ? "border-warn bg-warn/10 text-warn"
                  : "border-line bg-surface/40 text-foreground/75 hover:border-line-strong",
              )}
            >
              <svg
                aria-hidden
                viewBox="0 0 16 16"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <rect x="3" y="7" width="10" height="6" rx="1" />
                <path d="M5 7V5a3 3 0 0 1 6 0v2" />
              </svg>
              Private library
            </button>
          </div>
        </Field>

        <div className="flex justify-end border-t border-line pt-3">
          <Button type="submit" loading={searching}>
            Search
          </Button>
        </div>
      </form>

      {results && (
        <div ref={resultsTopRef} className="scroll-mt-4">
          <Pagination
            page={page}
            pageSize={pageSize}
            rowsOnPage={results.releases.length}
            loading={searching}
            position="top"
            onChange={(next) => runSearch(next)}
          />
        </div>
      )}

      {results && (
        <ResultsTable
          results={results.releases}
          selectedRows={selectedRows}
          onToggleRow={toggleRow}
          onGrab={(r) => grabMut.mutate(r)}
          grabbing={grabMut.isPending ? grabMut.variables?.guid : undefined}
          destination={destination}
        />
      )}

      {/* Pagination footer. We don't get a "total" count back from
          Newznab indexers reliably (some do return `total` on the
          channel, but with multi-indexer fan-out they'd disagree), so
          we run the buttons off page-state alone:
            * "Prev" disabled on page 0.
            * "Next" disabled when the current page returned fewer
              rows than the per-indexer limit (clear signal we hit
              the end).
          With multiple indexers selected page boundaries are
          approximate — that's flagged in the hint copy. */}
      {results && (
        <Pagination
          page={page}
          pageSize={pageSize}
          rowsOnPage={results.releases.length}
          loading={searching}
          position="bottom"
          onChange={async (next) => {
            await runSearch(next);
            resultsTopRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }}
        />
      )}
    </div>
  );
}

function Pagination({
  page,
  pageSize,
  rowsOnPage,
  loading,
  position,
  onChange,
}: {
  page: number;
  pageSize: number;
  rowsOnPage: number;
  loading: boolean;
  position: "top" | "bottom";
  onChange: (next: number) => void;
}) {
  const start = page * pageSize;
  const end = start + rowsOnPage;
  // "Likely more" heuristic: if the current page hit the limit cap
  // we assume there's another page. The user may click Next and get
  // an empty page — we toast that case so it doesn't feel broken.
  const hasNext = rowsOnPage >= pageSize;
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-between gap-3 border-line sm:flex-row",
        position === "top" ? "border-b pb-3" : "border-t pt-4",
      )}
    >
      <div className="font-mono text-xs text-foreground/55">
        {rowsOnPage === 0
          ? "No results on this page"
          : `Showing ${start + 1}–${end} (page ${page + 1})`}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={page === 0 || loading}
          loading={loading}
          onClick={() => onChange(page - 1)}
        >
          ← Prev
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!hasNext || loading}
          loading={loading}
          onClick={() => onChange(page + 1)}
        >
          Next →
        </Button>
      </div>
    </div>
  );
}

function ResultsTable({
  results,
  selectedRows,
  onToggleRow,
  onGrab,
  grabbing,
  destination,
}: {
  results: SearchResultRelease[];
  selectedRows: Set<string>;
  onToggleRow: (guid: string) => void;
  onGrab: (release: SearchResultRelease) => void;
  grabbing: string | undefined;
  destination: "public" | "private";
}) {
  if (results.length === 0) {
    return (
      <div className="rounded border border-dashed border-line p-6 text-sm text-foreground/60">
        No results.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {/* Migration 0062: persistent banner above the results table while
          private destination is active, so it's impossible to forget
          the toggle is on while clicking through rows. */}
      {destination === "private" && (
        <div
          role="status"
          className="flex items-center gap-2 rounded border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn"
        >
          <svg
            aria-hidden
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
          >
            <rect x="3" y="7" width="10" height="6" rx="1" />
            <path d="M5 7V5a3 3 0 0 1 6 0v2" />
          </svg>
          Downloads from this page route to the{" "}
          <strong>private library</strong>. Toggle the destination
          above to switch back.
        </div>
      )}
      {/* Mobile card list — the wide table is a non-starter on a 390px
          viewport (10 columns). Each card stacks the same fields top-
          to-bottom with the title leading and the action buttons last. */}
      <ul className="space-y-2 md:hidden">
        {results.map((r) => (
          <li
            key={`mc-${r.indexer_id}:${r.guid}`}
            className={cn(
              "rounded border border-line bg-surface/30 p-3",
              selectedRows.has(r.guid) && "border-accent/40 bg-accent/5",
            )}
          >
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-1 cursor-pointer"
                checked={selectedRows.has(r.guid)}
                onChange={() => onToggleRow(r.guid)}
                aria-label="Select row"
              />
              <div className="min-w-0 flex-1">
                {r.info_url ? (
                  <a
                    href={r.info_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block break-words font-mono text-xs leading-snug hover:text-accent"
                  >
                    {r.title}
                  </a>
                ) : (
                  <span className="block break-words font-mono text-xs leading-snug">
                    {r.title}
                  </span>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground/65">
                  <ProtocolBadge protocol={r.protocol} />
                  <span className="tabular-nums">{formatAge(r.publish_date)}</span>
                  <span className="tabular-nums">{formatBytes(r.size)}</span>
                  <span>{r.indexer_name}</span>
                  {r.grabs != null && (
                    <span className="tabular-nums">grabs: {r.grabs}</span>
                  )}
                  {r.categories.length > 0 && (
                    <span title={r.categories.join(", ")}>
                      {categoryNames(r.categories).join(", ")}
                    </span>
                  )}
                </div>
                {(r.flags.length > 0 ||
                  (r.protocol === "torrent" && r.seeders != null)) && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.flags.map((f) => (
                      <span
                        key={f}
                        className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] uppercase text-yellow-300"
                      >
                        {f}
                      </span>
                    ))}
                    {r.protocol === "torrent" && r.seeders != null && (
                      <span
                        className="rounded bg-surface-2/60 px-1.5 py-0.5 text-[10px] text-foreground/70"
                        title="seeders / leechers"
                      >
                        {r.seeders}↑ {r.leechers ?? 0}↓
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <a
                href={searchApi.saveNzbUrl(
                  r.indexer_id,
                  r.download_url,
                  `${sanitiseFilename(r.title)}.nzb`,
                )}
                download
              >
                <Button type="button" size="sm" variant="ghost">
                  Save
                </Button>
              </a>
              <Button
                type="button"
                size="sm"
                variant={destination === "private" ? "danger" : "secondary"}
                loading={grabbing === r.guid}
                onClick={() => onGrab(r)}
              >
                {destination === "private"
                  ? "Download (private)"
                  : "Download"}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop table — same content, denser layout. */}
      <div className="hidden overflow-x-auto rounded border border-line md:block">
      <table className="w-full text-sm">
        <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
          <tr>
            <th className="w-8 px-2 py-2"></th>
            <th className="px-3 py-2 font-medium">Proto</th>
            <th className="px-3 py-2 font-medium tabular-nums">Age</th>
            <th className="px-3 py-2 font-medium">Title</th>
            <th className="px-3 py-2 font-medium">Indexer</th>
            <th className="px-3 py-2 font-medium tabular-nums">Size</th>
            <th className="px-3 py-2 font-medium tabular-nums">Grabs</th>
            <th className="px-3 py-2 font-medium">Cat.</th>
            <th className="px-3 py-2 font-medium">Flags</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr
              key={`${r.indexer_id}:${r.guid}`}
              className={cn(
                "border-t border-line hover:bg-surface/30",
                selectedRows.has(r.guid) && "bg-accent/5",
              )}
            >
              <td className="px-2 py-2">
                <input
                  type="checkbox"
                  className="cursor-pointer"
                  checked={selectedRows.has(r.guid)}
                  onChange={() => onToggleRow(r.guid)}
                />
              </td>
              <td className="px-3 py-2">
                <ProtocolBadge protocol={r.protocol} />
              </td>
              <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                {formatAge(r.publish_date)}
              </td>
              <td className="max-w-md px-3 py-2">
                {r.info_url ? (
                  <a
                    href={r.info_url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate font-mono text-xs hover:text-accent"
                    title={r.title}
                  >
                    {r.title}
                  </a>
                ) : (
                  <span
                    className="block truncate font-mono text-xs"
                    title={r.title}
                  >
                    {r.title}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-foreground/70">
                {r.indexer_name}
              </td>
              <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                {formatBytes(r.size)}
              </td>
              <td className="px-3 py-2 text-xs tabular-nums text-foreground/70">
                {r.grabs ?? "—"}
              </td>
              <td
                className="px-3 py-2 text-xs text-foreground/70"
                /* Title keeps the raw numeric ids around for users
                   debugging an indexer's category mapping. */
                title={r.categories.length > 0 ? r.categories.join(", ") : undefined}
              >
                {r.categories.length === 0
                  ? "—"
                  : categoryNames(r.categories).join(", ")}
              </td>
              <td className="px-3 py-2 text-xs">
                <div className="flex flex-wrap gap-1">
                  {r.flags.map((f) => (
                    <span
                      key={f}
                      className="rounded bg-yellow-500/20 px-1.5 py-0.5 text-[10px] uppercase text-yellow-300"
                    >
                      {f}
                    </span>
                  ))}
                  {r.protocol === "torrent" && r.seeders != null && (
                    <span
                      className="rounded bg-surface-2/60 px-1.5 py-0.5 text-[10px] text-foreground/70"
                      title="seeders / leechers"
                    >
                      {r.seeders}↑ {r.leechers ?? 0}↓
                    </span>
                  )}
                </div>
              </td>
              <td className="space-x-1 px-3 py-2 text-right whitespace-nowrap">
                <a
                  href={searchApi.saveNzbUrl(
                    r.indexer_id,
                    r.download_url,
                    `${sanitiseFilename(r.title)}.nzb`,
                  )}
                  download
                >
                  <Button type="button" size="sm" variant="ghost">
                    Save
                  </Button>
                </a>
                <Button
                  type="button"
                  size="sm"
                  variant={destination === "private" ? "danger" : "secondary"}
                  loading={grabbing === r.guid}
                  onClick={() => onGrab(r)}
                >
                  {destination === "private" ? "Download (private)" : "Download"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function ProtocolBadge({ protocol }: { protocol: "torrent" | "usenet" }) {
  if (protocol === "usenet") {
    return (
      <span className="rounded bg-blue-500/20 px-1.5 py-0.5 text-[10px] uppercase text-blue-300">
        NZB
      </span>
    );
  }
  return (
    <span className="rounded bg-purple-500/20 px-1.5 py-0.5 text-[10px] uppercase text-purple-300">
      Torrent
    </span>
  );
}

function formatAge(iso: string | null): string {
  if (!iso) return "—";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "—";
  const days = (Date.now() - ts) / 86_400_000;
  if (days < 1) return "<1d";
  if (days < 30) return `${Math.round(days)}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}y`;
}

function formatBytes(n: number): string {
  if (n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/** UI accepts whole MB; backend wants bytes. Empty string + non-numeric
 *  + negative all coalesce to `null` ("no bound"); positive integers
 *  multiply. Decimals are accepted (1.5 GB → "1500" MB → 1.5e9 bytes). */
function parseMbToBytes(input: string): number | null {
  const t = input.trim();
  if (t === "") return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 1024 * 1024);
}

function sanitiseFilename(s: string): string {
  return s
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}
